import React, { useEffect } from "react";
import { Flex, Text } from "@fluentui/react-northstar";
import { useQuery } from "react-query";
import { Company, Scope } from "../models";
import ReportsService from "../service/ReportsService";
import { View360ReportTable } from "view360Card/components/view360ReportTable/View360ReportTable";
import pl from "shared/messages/pl";
import { SalesAndActivitySectionDataSource } from "./SalesAndActivitySection";
import FailedToFetchReportError from "../error/FailedToFetchReportError";

export interface ActivitySubSectionProps {
    dataSource: SalesAndActivitySectionDataSource;
    id: number;
    salesPersonIntermediaryNumber?: string;
    scope: Scope | undefined;
    company: Company;
    selectedSegments: string[];
    selectedYears: string[];
    selectedMode: string;
}

export const ActivitySubSection: React.FC<ActivitySubSectionProps> = (props) => {
    const queryKey = `activity_${props.company.code}`;

    const { isFetching, isError, data, refetch } = useQuery(queryKey, async () => {
        switch (props.dataSource) {
            case SalesAndActivitySectionDataSource.salesPerson:
                return await ReportsService.getSalesPersonActivityReport(
                    props.id,
                    props.salesPersonIntermediaryNumber,
                    props.scope,
                    props.company.code,
                    props.selectedSegments,
                    props.selectedYears,
                    props.selectedMode
                );

            case SalesAndActivitySectionDataSource.intermediary:
                return await ReportsService.getIntermediaryActivityReport(
                    props.id,
                    props.scope,
                    props.company.code,
                    props.selectedSegments,
                    props.selectedYears,
                    props.selectedMode
                );

            default:
                throw new Error("Activity report data source has not been implemented.");
        }
    });

    useEffect(() => {
        const init = async () => {
            await refetch();
        };

        init();
    }, [
        props.salesPersonIntermediaryNumber,
        props.scope?.code,
        props.selectedSegments.length,
        props.selectedYears.length,
        props.selectedMode,
    ]);

    if (isError) {
        return (
            <FailedToFetchReportError
                message={pl.view360Card.reports.errors.failedToFetchActivityReportErrorMessage(props.company.name)}
            />
        );
    }

    return (
        <Flex column gap="gap.small">
            <Text size="medium" weight="semibold">
                {pl.view360Card.reports.activitySubSectionTitle(props.company.name)}
            </Text>
            <View360ReportTable reportData={data} />
        </Flex>
    );
};
